import { Box, Button, HStack, Link, Stack, Text, Tooltip, useCustomToast, useDisclosure } from '@elkaso-app/web-design';
import { ReconciliationSupplierDetail, useGenerateMonthlyInvoiceApi } from 'apis/reconciliation';
import dayjs from 'dayjs';
import { UploadMonthlyInvoiceModal } from 'pages/reconciliation/components/upload-monthly-invoice-modal';
import { formatAed } from 'pages/reconciliation/utils/format';
import { getLatestMonthlyInvoice, isMonthlyInvoiceStale } from 'pages/reconciliation/utils/invoices';
import { useQueryClient } from 'react-query';

export interface IMonthlyInvoiceCard {
  month: string;
  detail: ReconciliationSupplierDetail;
  /** Opens the orders modal supplier-wide with "Unpaid only" preset — exactly the set the
   * monthly invoice will bill, which makes the generate action predictable. */
  onViewUnpaidOrders?: () => void;
}

const SummaryRow = ({
  label,
  value,
  isBold,
  tooltip,
}: {
  label: string;
  value: number;
  isBold?: boolean;
  tooltip?: string;
}) => (
  <HStack justify='space-between'>
    <Tooltip label={tooltip} placement='top' hasArrow isDisabled={!tooltip}>
      <Text
        fontSize='sm'
        color={isBold ? 'gray.700' : 'gray.500'}
        fontWeight={isBold ? 'bold' : 'normal'}
        borderBottom={tooltip ? '1px dotted' : undefined}
        borderColor='gray.300'
        as='span'>
        {label}
      </Text>
    </Tooltip>
    <Text fontSize='sm' fontWeight={isBold ? 'bold' : 'medium'} color='gray.700' whiteSpace='nowrap'>
      {formatAed(value)}
    </Text>
  </HStack>
);

/** Mini financial summary mirroring the PDF (Subtotal / VAT / Total / Previous balance / Balance
 * due) so ops can sanity-check without opening the file. The same POST both generates and
 * regenerates — the BE keeps one live monthly invoice for the month. Kept enabled even when
 * everything might already be paid: unpaid-order existence isn't reliably knowable client-side
 * (invoiceless orders are invisible here), so the BE's 400 message surfaces verbatim instead.
 *
 * Upload is the escape hatch when Finance issues the invoice themselves; it replaces an unpaid
 * generated one, so it stays a secondary action rather than a competing primary button. */
export const MonthlyInvoiceCard = ({ month, detail, onViewUnpaidOrders }: IMonthlyInvoiceCard) => {
  const toast = useCustomToast();
  const queryClient = useQueryClient();

  const uploadModal = useDisclosure();

  const invoices = detail.invoices ?? [];
  const monthly = getLatestMonthlyInvoice(invoices);
  const isClosed = detail.status === 'closed';
  const isStale = monthly ? isMonthlyInvoiceStale(monthly, invoices) : false;
  // Upload replaces the live monthly invoice, and the BE refuses to replace a paid
  // one. Closed months ARE allowed here (unlike generation) — attaching a
  // Finance-issued original after close is exactly what this is for.
  const uploadDisabledReason = monthly?.isPaid ? 'Cannot replace a paid invoice' : null;

  const { mutate: generateMonthly, isLoading: isGenerating } = useGenerateMonthlyInvoiceApi({
    onSuccess: ({ invoice }) => {
      toast({ description: `Monthly invoice ${invoice.invoiceNumber} generated`, status: 'success' });
      queryClient.invalidateQueries(['reconciliation-supplier-details', month, detail.supplierId]);
      queryClient.invalidateQueries(['reconciliation-suppliers']);
      queryClient.invalidateQueries(['reconciliation-summary', month]);
    },
  });

  return (
    <Stack spacing='sm' border='1px solid' borderColor='gray.100' borderRadius='md' p='sm' bg='white'>
      <HStack justify='space-between'>
        <Text fontSize='xs' fontWeight='semibold' color='gray.500' textTransform='uppercase' letterSpacing='0.4px'>
          Monthly invoice
        </Text>
        {onViewUnpaidOrders && (
          <Button size='xs' variant='link' onClick={onViewUnpaidOrders}>
            View unpaid orders
          </Button>
        )}
      </HStack>

      {monthly ? (
        <>
          <HStack justify='space-between' flexWrap='wrap' rowGap='xs'>
            <Link
              href={monthly.url}
              target='_blank'
              rel='noopener noreferrer'
              color='red.500'
              fontSize='sm'
              fontWeight='semibold'>
              {monthly.invoiceNumber}
            </Link>
            <Text fontSize='xs' color='gray.400' whiteSpace='nowrap'>
              Generated {dayjs(monthly.createdAt).format('DD MMM YYYY')}
            </Text>
          </HStack>

          {isStale && (
            <Text fontSize='xs' color='#c77c00'>
              Payments changed after this invoice was generated — regenerate to refresh it.
            </Text>
          )}

          <Stack spacing='2px'>
            <SummaryRow label='Subtotal' value={monthly.netAmount} />
            <SummaryRow label='VAT' value={monthly.vatAmount} />
            <SummaryRow label='Total' value={monthly.totalAmount} />
            <SummaryRow
              label='Previous balance'
              value={monthly.previousBalance ?? 0}
              tooltip='Unpaid amounts from prior closed months — VAT already included. An open older month won’t appear until it is closed.'
            />
            <SummaryRow label='Balance due' value={monthly.totalAmount + (monthly.previousBalance ?? 0)} isBold />
          </Stack>
        </>
      ) : (
        <Text fontSize='sm' color='gray.500'>
          No monthly invoice yet — it will bill this month’s unpaid orders as a single platform fee, plus any previous
          balance.
        </Text>
      )}

      <Tooltip label='Month is closed' placement='top' hasArrow isDisabled={!isClosed}>
        {/* span wrapper: a disabled button swallows the hover events the tooltip needs */}
        <Box as='span' w='100%'>
          <Button
            w='100%'
            size='sm'
            colorScheme='red'
            variant={monthly ? 'outline' : 'solid'}
            isDisabled={isClosed}
            isLoading={isGenerating}
            onClick={() => generateMonthly({ month, supplierId: detail.supplierId })}>
            {monthly ? 'Regenerate monthly invoice' : 'Generate monthly invoice'}
          </Button>
        </Box>
      </Tooltip>

      <Tooltip label={uploadDisabledReason ?? ''} placement='top' hasArrow isDisabled={!uploadDisabledReason}>
        <Box as='span' alignSelf='center'>
          <Button size='xs' variant='link' isDisabled={!!uploadDisabledReason} onClick={uploadModal.onOpen}>
            {monthly ? 'Replace with an uploaded PDF…' : 'Upload invoice PDF…'}
          </Button>
        </Box>
      </Tooltip>

      <UploadMonthlyInvoiceModal
        month={month}
        detail={detail}
        isOpen={uploadModal.isOpen}
        onClose={uploadModal.onClose}
      />
    </Stack>
  );
};
